import type { RichTextBlock, RichTextInline, RichTextVideoBlock } from "./types";

const inlinePattern = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;

const parseInline = (text: string): RichTextInline[] => {
  const nodes: RichTextInline[] = [];
  let cursor = 0;

  for (const match of text.matchAll(inlinePattern)) {
    const token = match[0];
    const index = match.index || 0;

    if (index > cursor) {
      nodes.push({ type: "text", text: text.slice(cursor, index) });
    }

    if (token.startsWith("**") && token.endsWith("**")) {
      nodes.push({ type: "strong", text: token.slice(2, -2) });
    } else if (token.startsWith("`") && token.endsWith("`")) {
      nodes.push({ type: "code", text: token.slice(1, -1) });
    } else {
      const linkMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (linkMatch) {
        nodes.push({ type: "link", text: linkMatch[1], href: linkMatch[2] });
      } else {
        nodes.push({ type: "text", text: token });
      }
    }

    cursor = index + token.length;
  }

  if (cursor < text.length) {
    nodes.push({ type: "text", text: text.slice(cursor) });
  }

  return nodes;
};

const parseVideoBlock = (lines: string[]): RichTextVideoBlock | null => {
  const values: Record<string, string> = {};

  for (const line of lines) {
    const match = line.match(/^([a-zA-Z][\w-]*):\s*(.*)$/);
    if (!match) continue;
    values[match[1]] = match[2].trim().replace(/^["']|["']$/g, "");
  }

  if (!values.src) return null;

  return {
    type: "video",
    src: values.src,
    poster: values.poster,
    caption: values.caption,
  };
};

const flushParagraph = (blocks: RichTextBlock[], paragraph: string[]) => {
  if (paragraph.length === 0) return;
  const text = paragraph.join(" ").trim();
  if (text) {
    blocks.push({ type: "paragraph", children: parseInline(text) });
  }
  paragraph.length = 0;
};

const flushList = (blocks: RichTextBlock[], listItems: string[]) => {
  if (listItems.length === 0) return;
  blocks.push({
    type: "list",
    items: listItems.map((item) => parseInline(item)),
  });
  listItems.length = 0;
};

export const parseRichText = (source: string): RichTextBlock[] => {
  const blocks: RichTextBlock[] = [];
  const paragraph: string[] = [];
  const listItems: string[] = [];
  const lines = source.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");

  for (let index = 0; index < lines.length; index += 1) {
    const rawLine = lines[index];
    const line = rawLine.trim();

    if (!line) {
      flushParagraph(blocks, paragraph);
      flushList(blocks, listItems);
      continue;
    }

    if (line === ":::video") {
      flushParagraph(blocks, paragraph);
      flushList(blocks, listItems);

      const videoLines: string[] = [];
      index += 1;
      while (index < lines.length && lines[index].trim() !== ":::") {
        videoLines.push(lines[index].trim());
        index += 1;
      }

      const video = parseVideoBlock(videoLines);
      if (video) blocks.push(video);
      continue;
    }

    const listMatch = line.match(/^[-*]\s+(.+)$/);
    if (listMatch) {
      flushParagraph(blocks, paragraph);
      listItems.push(listMatch[1]);
      continue;
    }

    flushList(blocks, listItems);
    paragraph.push(line);
  }

  flushParagraph(blocks, paragraph);
  flushList(blocks, listItems);

  return blocks;
};
